/**
 * DirectLinkDialog.tsx
 * 直链管理对话框组件
 *
 * 功能:
 * - 创建/删除文件直链
 * - 设置有效期或永久有效
 * - 复制直链URL
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/useToast';
import { Link, Copy, Trash2, RefreshCw, ExternalLink, Check, Infinity } from 'lucide-react';
import { directLinkApi, type DirectLinkInfo } from '@/services/api';
import { getErrorMessage } from '@/services/errorHandler';
import { decodeFileName } from '@/utils';
import { translate as t, useI18n } from '@/i18n';
import { getLanguage } from '@/i18n';

interface DirectLinkDialogProps {
  fileId: string;
  fileName: string;
  onClose: () => void;
}

export function DirectLinkDialog({ fileId, fileName, onClose }: DirectLinkDialogProps) {
  useI18n();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);
  const [directLink, setDirectLink] = useState<DirectLinkInfo | null>(null);
  const [expiresDays, setExpiresDays] = useState<number | ''>(7);
  const [isPermanent, setIsPermanent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [linkType, setLinkType] = useState<'download' | 'preview'>('download');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retryAction, setRetryAction] = useState<'save' | 'delete' | 'copy' | null>(null);
  const requestVersion = useRef(0);
  const pendingRequest = useRef(false);
  const copying = useRef(false);

  const loadDirectLink = useCallback(async () => {
    if (pendingRequest.current) return;
    const version = ++requestVersion.current;
    pendingRequest.current = true;
    setIsLoading(true);
    setIsPending(false);
    setLoadError(null);
    setActionError(null);
    setRetryAction(null);
    setDirectLink(null);
    setIsPermanent(false);
    setExpiresDays(7);
    setCopied(false);
    try {
      const res = await directLinkApi.get(fileId);
      if (!res.data.success) throw new Error(res.data.error?.message || t("加载直链失败，请重试"));
      if (version !== requestVersion.current) return;
      setDirectLink(res.data.data ?? null);
      if (res.data.data) {
        setIsPermanent(res.data.data.isPermanent);
        if (!res.data.data.isPermanent && res.data.data.expiresAt) {
          const days = Math.ceil((new Date(res.data.data.expiresAt).getTime() - Date.now()) / 86400000);
          setExpiresDays(days > 0 ? days : 7);
        }
      }
    } catch (e) {
      console.error('Failed to load direct link:', e);
      if (version === requestVersion.current) setLoadError(getErrorMessage(e));
    } finally {
      if (version === requestVersion.current) {
        pendingRequest.current = false;
        setIsLoading(false);
      }
    }
  }, [fileId]);

  useEffect(() => {
    void loadDirectLink();
    return () => {
      requestVersion.current += 1;
      pendingRequest.current = false;
    };
  }, [loadDirectLink]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const isBusy = isLoading || isPending || isCopying;
  const daysError = isPermanent
    ? null
    : expiresDays === '' || !Number.isSafeInteger(expiresDays) || expiresDays <= 0
      ? t("请输入大于 0 的整数天数")
      : !Number.isFinite(new Date(Date.now() + expiresDays * 86400000).getTime())
        ? t("有效天数过大，请输入较小的天数")
        : null;
  const isExpired = Boolean(
    directLink &&
    !directLink.isPermanent &&
    directLink.expiresAt &&
    new Date(directLink.expiresAt).getTime() <= Date.now()
  );
  const selectedUrl = (() => {
    if (!directLink) return '';
    const url = new URL(directLink.directUrl, window.location.origin);
    if (linkType === 'preview') url.pathname = `${url.pathname.replace(/\/$/, '')}/preview`;
    return url.href;
  })();

  const reportActionError = (error: unknown, action: 'save' | 'delete' | 'copy') => {
    const message = getErrorMessage(error);
    setActionError(message);
    setRetryAction(action);
    toast({ title: t("操作失败"), description: message, variant: 'destructive' });
  };

  const handleSave = async () => {
    if (pendingRequest.current || copying.current || isLoading || loadError) return;
    if (daysError) {
      toast({ title: t("有效期无效"), description: daysError, variant: 'destructive' });
      return;
    }
    const version = requestVersion.current;
    pendingRequest.current = true;
    setIsPending(true);
    setActionError(null);
    setRetryAction(null);
    try {
      const expiresAt = isPermanent ? null : new Date(Date.now() + Number(expiresDays) * 86400000).toISOString();
      const res = directLink
        ? await directLinkApi.update(fileId, expiresAt)
        : await directLinkApi.create(fileId, expiresAt);
      if (!res.data.success || !res.data.data) throw new Error(res.data.error?.message || t("保存直链失败，请重试"));
      void queryClient.invalidateQueries({ queryKey: ['direct-links'] });
      if (version !== requestVersion.current) return;
      setDirectLink(res.data.data);
      setCopied(false);
      toast({ title: directLink ? t("有效期已更新，原链接保持不变") : t("直链已创建") });
    } catch (e) {
      console.error('Failed to save direct link:', e);
      if (version === requestVersion.current) reportActionError(e, 'save');
    } finally {
      if (version === requestVersion.current) {
        pendingRequest.current = false;
        setIsPending(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!directLink || pendingRequest.current || copying.current) return;
    if (!confirm(t("确定要删除此直链吗？删除后该链接将立即失效。"))) return;
    const version = requestVersion.current;
    pendingRequest.current = true;
    setIsPending(true);
    setActionError(null);
    setRetryAction(null);
    try {
      const res = await directLinkApi.delete(fileId);
      if (!res.data.success) throw new Error(res.data.error?.message || t("删除直链失败，请重试"));
      void queryClient.invalidateQueries({ queryKey: ['direct-links'] });
      if (version !== requestVersion.current) return;
      setDirectLink(null);
      setIsPermanent(false);
      setExpiresDays(7);
      setCopied(false);
      toast({ title: t("直链已删除") });
    } catch (e) {
      console.error('Failed to delete direct link:', e);
      if (version === requestVersion.current) reportActionError(e, 'delete');
    } finally {
      if (version === requestVersion.current) {
        pendingRequest.current = false;
        setIsPending(false);
      }
    }
  };

  const handleCopy = async () => {
    if (!selectedUrl || isExpired || pendingRequest.current || copying.current) return;
    const version = requestVersion.current;
    copying.current = true;
    setIsCopying(true);
    setActionError(null);
    setRetryAction(null);
    try {
      await navigator.clipboard.writeText(selectedUrl);
      if (version !== requestVersion.current) return;
      setCopied(true);
      toast({ title: t("{{p0}}链接已复制", { p0: linkType === 'preview' ? '预览' : '下载' }) });
    } catch (e) {
      console.error('Failed to copy:', e);
      if (version === requestVersion.current) {
        reportActionError(new Error(t("复制失败，请重试或选中链接手动复制")), 'copy');
      }
    } finally {
      copying.current = false;
      setIsCopying(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return t("永久有效");
    return new Date(dateStr).toLocaleString(getLanguage(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => {
        if (!isPending && !isCopying) onClose();
      }}
    >
      <div
        className="bg-card border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="direct-link-title"
        aria-busy={isBusy}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <Link className="h-4 w-4 text-primary" />
          <h2 id="direct-link-title" className="text-lg font-semibold">
            {t("文件直链")}</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4 break-all">
          {t("文件：")}<span className="font-medium text-foreground">{decodeFileName(fileName)}</span>
        </p>

        {actionError && (
          <div role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
            <p className="text-sm text-destructive">{t(actionError)}</p>
            <Button
              size="sm"
              variant="outline"
              disabled={
                isBusy || (retryAction === 'save' && Boolean(daysError)) || (retryAction === 'copy' && isExpired)
              }
              onClick={retryAction === 'delete' ? handleDelete : retryAction === 'copy' ? handleCopy : handleSave}
            >
              {t("重试")}</Button>
          </div>
        )}

        {isLoading ? (
          <div role="status" className="flex items-center justify-center gap-2 py-8">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t("正在加载直链...")}</span>
          </div>
        ) : loadError ? (
          <div className="space-y-4">
            <div role="alert" className="text-sm text-destructive">
              <p className="font-medium">{t("加载直链失败")}</p>
              <p className="mt-1">{t(loadError)}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                {t("关闭")}</Button>
              <Button onClick={loadDirectLink} disabled={isBusy}>
                {t("重新加载")}</Button>
            </div>
          </div>
        ) : directLink ? (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex gap-2 mb-2" role="group" aria-label={t("链接用途")}>
                {(['download', 'preview'] as const).map((type) => (
                  <Button
                    key={type}
                    size="sm"
                    variant={linkType === type ? 'default' : 'outline'}
                    aria-pressed={linkType === type}
                    disabled={isBusy}
                    onClick={() => {
                      setLinkType(type);
                      setCopied(false);
                    }}
                  >
                    {type === 'download' ? t("下载链接") : t("预览链接")}
                  </Button>
                ))}
              </div>
              <label htmlFor="direct-link-url" className="text-xs text-muted-foreground mb-1 block">
                {linkType === 'download' ? t("下载地址") : t("预览地址")}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="direct-link-url"
                  readOnly
                  value={selectedUrl}
                  onFocus={(e) => e.target.select()}
                  className="text-xs font-mono flex-1 min-w-0"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopy}
                  disabled={isBusy || isExpired}
                  title={t("复制链接")}
                  aria-label={t("复制链接")}
                  className="shrink-0"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => window.open(selectedUrl, '_blank', 'noopener,noreferrer')}
                  disabled={isBusy || isExpired}
                  title={t("在新窗口打开")}
                  aria-label={t("在新窗口打开")}
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {linkType === 'download'
                  ? t("访问后直接下载文件，无需登录。")
                  : t("尝试在浏览器中预览；不支持的格式可能仍会下载。")}
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("过期时间：")}</span>
              {directLink.isPermanent ? (
                <span className="flex items-center gap-1 text-green-600 font-medium">
                  <Infinity className="h-3 w-3" />
                  {t("永久有效")}</span>
              ) : (
                <span className={isExpired ? 'text-destructive' : undefined}>
                  {formatDate(directLink.expiresAt)}
                  {isExpired ? t("（已过期）") : ''}
                </span>
              )}
            </div>
            {isExpired && (
              <p role="status" className="text-sm text-destructive">
                {t("此直链已过期，请更新有效期后再分享。")}</p>
            )}

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="permanent"
                  checked={isPermanent}
                  disabled={isBusy}
                  onChange={(e) => setIsPermanent(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="permanent" className="text-sm font-medium flex items-center gap-1">
                  <Infinity className="h-3 w-3" />
                  {t("永久有效")}</label>
              </div>

              {!isPermanent && (
                <div className="space-y-1.5">
                  <label htmlFor="direct-link-days" className="text-sm font-medium">
                    {t("新的有效天数")}</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      id="direct-link-days"
                      min={1}
                      step={1}
                      placeholder={t("输入天数")}
                      value={expiresDays}
                      disabled={isBusy}
                      aria-invalid={Boolean(daysError)}
                      aria-describedby={daysError ? 'direct-link-days-error' : undefined}
                      onChange={(e) => setExpiresDays(e.target.value ? Number(e.target.value) : '')}
                      className="flex-1"
                    />
                    <Button onClick={handleSave} disabled={isBusy || Boolean(daysError)}>
                      {isPending ? t("处理中...") : t("更新")}
                    </Button>
                  </div>
                </div>
              )}

              {isPermanent && (
                <Button onClick={handleSave} disabled={isBusy} className="w-full">
                  {isPending ? t("处理中...") : t("设为永久有效")}
                </Button>
              )}
              {daysError && (
                <p id="direct-link-days-error" role="alert" className="text-xs text-destructive">
                  {t(daysError)}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t("更新后的有效期从现在开始计算，不叠加原剩余时间；保留原 token，下载和预览链接均不变。")}</p>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="destructive" onClick={handleDelete} disabled={isBusy}>
                <Trash2 className="h-4 w-4 mr-2" />
                {t("删除直链")}</Button>
              <Button variant="outline" onClick={onClose} disabled={isBusy}>
                {t("关闭")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("创建直链后，任何人都可以通过该链接直接下载文件，无需登录。")}</p>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="permanent-new"
                  checked={isPermanent}
                  disabled={isBusy}
                  onChange={(e) => setIsPermanent(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="permanent-new" className="text-sm font-medium flex items-center gap-1">
                  <Infinity className="h-3 w-3" />
                  {t("永久有效")}</label>
              </div>

              {!isPermanent && (
                <div className="space-y-1.5">
                  <label htmlFor="direct-link-days-new" className="text-sm font-medium">
                    {t("有效期（天）")}</label>
                  <Input
                    type="number"
                    id="direct-link-days-new"
                    min={1}
                    step={1}
                    placeholder={t("默认 7 天")}
                    value={expiresDays}
                    disabled={isBusy}
                    aria-invalid={Boolean(daysError)}
                    aria-describedby={daysError ? 'direct-link-days-new-error' : undefined}
                    onChange={(e) => setExpiresDays(e.target.value ? Number(e.target.value) : '')}
                  />
                  {daysError && (
                    <p id="direct-link-days-new-error" role="alert" className="text-xs text-destructive">
                      {t(daysError)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={isBusy}>
                {t("取消")}</Button>
              <Button onClick={handleSave} disabled={isBusy || Boolean(daysError)}>
                {isPending ? t("创建中...") : t("创建直链")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
